import { createContext, useContext, type ReactNode } from 'react'
import { createBrowserShenAiClient, warmUpBrowserShenAi } from './browser-shenai-client'
import type { ShenAiVitalClient } from './shenai-types'

/**
 * A FACTORY rather than a client instance, for the reason `MindsetClientProvider` gives: the client
 * is built around a camera the flow opened inside the patient's tap and a canvas that does not exist
 * until the scan screen is rendered, so the seam is the thing that makes a client.
 */
export type ShenAiClientFactory = (options: {
  /** The camera the flow opened inside the patient's tap (docs/decisions.md D-24). */
  readonly stream?: MediaStream | null
  /** The canvas the vendor renders into; its pipeline is WebGL2 on a canvas. */
  readonly canvasId: string
  /**
   * Whether the vendor draws its face mesh. Omitted means yes, which is the single-vendor scan; a
   * hybrid scan passes false because that canvas is underneath an opaque video (docs/decisions.md
   * D-38).
   */
  readonly drawFaceMask?: boolean
  /**
   * Which session to run: the vendor's one-minute all-metrics preset (the default, and the
   * single-vendor scan), or the hybrid scan's thirty seconds of four metrics (docs/decisions.md D-40).
   */
  readonly measurement?: 'oneMinuteAllMetrics' | 'thirtySecondsAllMetrics'
  /**
   * The shape of the frames the camera is delivering, reported once, as width over height.
   *
   * READ OFF THE STREAM THIS CLIENT ALREADY HAS, and it changes nothing about when that stream is
   * opened or who opens it. The scan surface needs it to crop the vendor's own preview to its frame
   * (scanning-screen.tsx) and there is no `<video>` on this path to measure instead.
   */
  readonly onCameraAspect?: (widthOverHeight: number) => void
}) => ShenAiVitalClient

const ShenAiClientContext = createContext<ShenAiClientFactory | null>(null)

/**
 * Starts this vendor's runtime loading on the start screen, before the scan that will use it
 * (docs/decisions.md D-63). Separate from the factory because a factory makes a scan's client and this
 * makes none: what it starts, the next client the factory makes adopts.
 */
export type ShenAiWarmUp = () => void

const ShenAiWarmUpContext = createContext<ShenAiWarmUp>(() => {})

/**
 * Production renders with no `factory` prop and gets the real browser adapter; every unit and
 * component test passes a fake, so no test loads the 33 MB vendor bundle, opens a camera or reaches
 * `api.shen.ai`.
 */
export function ShenAiClientProvider({
  factory,
  warmUp,
  children,
}: {
  factory?: ShenAiClientFactory
  /**
   * Absent with a fake `factory` means no warm-up at all: the real one loads the 33 MB vendor bundle,
   * which is exactly what passing a fake is for not doing. A test that wants to see it called passes
   * its own.
   */
  warmUp?: ShenAiWarmUp
  children: ReactNode
}) {
  return (
    <ShenAiClientContext.Provider value={factory ?? createBrowserShenAiClient}>
      <ShenAiWarmUpContext.Provider value={warmUp ?? (factory ? () => {} : warmUpBrowserShenAi)}>
        {children}
      </ShenAiWarmUpContext.Provider>
    </ShenAiClientContext.Provider>
  )
}

export function useShenAiClientFactory(): ShenAiClientFactory {
  const factory = useContext(ShenAiClientContext)
  if (!factory) {
    throw new Error('useShenAiClientFactory() called outside a ShenAiClientProvider')
  }
  return factory
}

export function useShenAiWarmUp(): ShenAiWarmUp {
  return useContext(ShenAiWarmUpContext)
}
